<template>
  <div>
    <section class="hero">
      <div class="hero-body">
        <p class="title">{{ i18n.users[iso] }}</p>
        <p class="subtitle is-6 has-text-grey">{{ resumen }}</p>
      </div>
    </section>

    <!-- Filtros: la búsqueda y el orden los resuelve la API -->
    <div class="columns is-multiline is-vcentered">
      <div class="column is-12-mobile is-6-tablet is-4-desktop">
        <b-field :label="t.buscar[iso]" label-position="on-border">
          <b-input
            v-model="filtros.busqueda"
            :placeholder="t.buscarAyuda[iso]"
            type="search"
            icon="search"
            icon-pack="fas"
            @input="buscarConPausa"
          ></b-input>
        </b-field>
      </div>
      <div class="column is-6-mobile is-3-tablet is-2-desktop">
        <b-field :label="i18n.role[iso]" label-position="on-border">
          <b-select v-model="filtros.Rol" expanded @input="buscar">
            <option :value="null">{{ t.todos[iso] }}</option>
            <option v-for="rol in rolesOrdenados" :value="rol._id" :key="rol._id">
              {{ nombreRol(rol) }}{{ rol.Activo === false ? ` (${t.inactivo[iso]})` : "" }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-6-mobile is-3-tablet is-2-desktop">
        <b-field :label="i18n.status[iso]" label-position="on-border">
          <b-select v-model="filtros.Activo" expanded @input="buscar">
            <option :value="null">{{ t.todos[iso] }}</option>
            <option :value="true">{{ t.activos[iso] }}</option>
            <option :value="false">{{ t.inactivos[iso] }}</option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-12-mobile is-6-tablet is-2-desktop">
        <b-field label="Club" label-position="on-border">
          <b-select v-model="filtros.ClubId" expanded @input="buscar">
            <option :value="null">{{ t.todos[iso] }}</option>
            <option v-for="c in clubs" :value="c.Id" :key="c._id || c.Id">
              {{ c.Nombre }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-12-mobile is-6-tablet is-2-desktop">
        <b-button
          icon-left="user-plus"
          icon-pack="fas"
          type="is-success"
          expanded
          @click="abrirAlta"
          >{{ i18n.createUser[iso] }}</b-button
        >
      </div>
    </div>

    <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
      <b-button
        v-if="hayFiltros"
        size="is-small"
        type="is-text"
        icon-left="times"
        icon-pack="fas"
        @click="limpiarFiltros"
        >{{ t.limpiar[iso] }}</b-button
      >
      <span v-else></span>
      <b-field :label="t.porPagina[iso]" horizontal custom-class="is-small" class="mb-0">
        <b-select v-model="porPagina" size="is-small" @input="cambiarPorPagina">
          <option v-for="n in [10, 20, 50, 100]" :value="n" :key="n">{{ n }}</option>
        </b-select>
      </b-field>
    </div>

    <b-table
      :data="usuarios"
      :loading="cargando"
      paginated
      backend-pagination
      :total="total"
      :per-page="porPagina"
      :current-page.sync="pagina"
      @page-change="cambiarPagina"
      backend-sorting
      :default-sort="[orden.campo, orden.direccion]"
      @sort="ordenar"
      pagination-simple
      striped
      hoverable
      narrowed
      mobile-cards
      aria-next-label="Siguiente"
      aria-previous-label="Anterior"
      aria-page-label="Página"
      aria-current-label="Página actual"
    >
      <b-table-column field="Usuario" :label="i18n.user[iso]" sortable v-slot="props">
        {{ props.row.Usuario }}
        <b-tag v-if="esYo(props.row)" type="is-info is-light" size="is-small">{{ t.tu[iso] }}</b-tag>
      </b-table-column>

      <b-table-column field="Nombre" :label="i18n.fullname[iso]" sortable v-slot="props">
        {{ props.row.Nombre || "" }} {{ props.row.Apellido || "" }}
      </b-table-column>

      <b-table-column field="Email" :label="i18n.email[iso]" sortable v-slot="props">
        {{ props.row.Email }}
      </b-table-column>

      <b-table-column field="Rol" :label="i18n.role[iso]" v-slot="props">
        <b-tooltip
          v-if="props.row.Rol"
          :active="props.row.Rol.Activo === false"
          :label="t.rolInactivo[iso]"
          type="is-dark"
        >
          <b-tag :type="props.row.Rol.Activo === false ? 'is-light' : 'is-info'">
            {{ nombreRol(props.row.Rol) }}
          </b-tag>
        </b-tooltip>
        <span v-else class="has-text-grey">{{ t.sinRol[iso] }}</span>
      </b-table-column>

      <b-table-column field="ClubId" label="Club" v-slot="props">
        {{ nombreClub(props.row.ClubId) }}
      </b-table-column>

      <b-table-column field="Activo" :label="i18n.status[iso]" sortable centered v-slot="props">
        <b-tag v-if="props.row.Activo" type="is-success">{{ i18n.active[iso] }}</b-tag>
        <b-tag v-else type="is-danger">{{ i18n.inactive[iso] }}</b-tag>
      </b-table-column>

      <b-table-column
        field="FechaUltimoInicioSesion"
        :label="t.ultimoAcceso[iso]"
        sortable
        v-slot="props"
      >
        <b-tooltip
          v-if="props.row.FechaUltimoInicioSesion"
          :label="fechaCompleta(props.row.FechaUltimoInicioSesion)"
          type="is-dark"
        >
          {{ hace(props.row.FechaUltimoInicioSesion) }}
        </b-tooltip>
        <span v-else class="has-text-grey">{{ t.nunca[iso] }}</span>
      </b-table-column>

      <b-table-column field="FechaCreacion" :label="t.creado[iso]" sortable v-slot="props">
        <div>{{ props.row.CreadoPor || "—" }}</div>
        <div v-if="props.row.FechaCreacion" class="is-size-7 has-text-grey">
          {{ fechaCorta(props.row.FechaCreacion) }}
        </div>
      </b-table-column>

      <b-table-column :label="t.acciones[iso]" centered v-slot="props">
        <div class="buttons is-centered is-flex-wrap-nowrap">
          <b-tooltip :label="t.editar[iso]" type="is-warning">
            <b-button
              type="is-warning is-light"
              icon-left="pen"
              icon-pack="fas"
              size="is-small"
              @click="abrirEdicion(props.row)"
            ></b-button>
          </b-tooltip>
          <b-tooltip
            :label="
              esYo(props.row)
                ? t.noTuMismo[iso]
                : props.row.Activo
                ? t.inhabilitar[iso]
                : t.habilitar[iso]
            "
            :type="props.row.Activo ? 'is-danger' : 'is-success'"
          >
            <b-button
              :type="props.row.Activo ? 'is-danger is-light' : 'is-success is-light'"
              :icon-left="props.row.Activo ? 'ban' : 'check'"
              icon-pack="fas"
              size="is-small"
              :disabled="esYo(props.row)"
              @click="cambiarEstado(props.row)"
            ></b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <div class="has-text-centered has-text-grey py-5">
          {{ cargando ? "" : t.sinResultados[iso] }}
        </div>
      </template>
    </b-table>

    <b-modal
      v-model="modalUsuario"
      has-modal-card
      trap-focus
      :can-cancel="['escape', 'x']"
      aria-role="dialog"
      aria-modal
    >
      <template #default="props">
        <modal-create-user-system
          :roles="roles"
          :clubs="clubs"
          :languages="catalogues.CatIdiomas"
          :userToEdit="usuarioSeleccionado"
          @refreshUsers="listarUsuarios"
          @close="props.close"
        ></modal-create-user-system>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import i18n from "../../utils/i18n";
import ModalCreateUserSystem from "./ModalCreateUserSystem.vue";

const API_URL = process.env.VUE_APP_API_URL;
const CLAVE_POR_PAGINA = "perPageSystemUsers";

const t = {
  buscar: { es: "Buscar", en: "Search" },
  buscarAyuda: { es: "Nombre, correo o usuario", en: "Name, email or username" },
  todos: { es: "Todos", en: "All" },
  activos: { es: "Activos", en: "Active" },
  inactivos: { es: "Inactivos", en: "Inactive" },
  inactivo: { es: "inactivo", en: "inactive" },
  limpiar: { es: "Limpiar filtros", en: "Clear filters" },
  porPagina: { es: "Mostrar", en: "Show" },
  tu: { es: "Tú", en: "You" },
  rolInactivo: { es: "Este rol está inactivo: el usuario no puede entrar", en: "This role is inactive: the user can't log in" },
  sinRol: { es: "Sin rol", en: "No role" },
  ultimoAcceso: { es: "Último acceso", en: "Last login" },
  nunca: { es: "Nunca", en: "Never" },
  creado: { es: "Creado por", en: "Created by" },
  acciones: { es: "Acciones", en: "Actions" },
  editar: { es: "Editar", en: "Edit" },
  inhabilitar: { es: "Inhabilitar", en: "Disable" },
  habilitar: { es: "Habilitar", en: "Enable" },
  noTuMismo: { es: "No puedes inhabilitar tu propio usuario", en: "You can't disable your own user" },
  sinResultados: { es: "No hay usuarios con esos filtros", en: "No users match these filters" },
  inhabilitado: { es: "Usuario inhabilitado", en: "User disabled" },
  habilitado: { es: "Usuario habilitado", en: "User enabled" },
  errorLista: { es: "Error al consultar usuarios", en: "Error loading users" },
  sesion: { es: "Tu sesión ya no es válida, vuelve a iniciar sesión", en: "Your session expired, please log in again" },
};

const escaparHtml = (texto) =>
  String(texto || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const leerPorPagina = () => {
  try {
    const n = parseInt(localStorage.getItem(CLAVE_POR_PAGINA), 10);
    return [10, 20, 50, 100].includes(n) ? n : 20;
  } catch (e) {
    return 20;
  }
};

export default {
  name: "SystemUsers",
  components: { ModalCreateUserSystem },
  data() {
    return {
      i18n,
      t,
      iso: localStorage.getItem("iso") || "es",
      miId: localStorage.getItem("userId"),

      usuarios: [],
      total: 0,
      cargando: false,
      pagina: 1,
      porPagina: leerPorPagina(),
      orden: { campo: "FechaCreacion", direccion: "desc" },
      filtros: { busqueda: "", Rol: null, Activo: null, ClubId: null },
      pausa: null,
      consulta: 0,

      roles: [],
      clubs: [],
      catalogues: {},

      modalUsuario: false,
      usuarioSeleccionado: null,
    };
  },
  computed: {
    rolesOrdenados() {
      return [...this.roles].sort((a, b) => this.nombreRol(a).localeCompare(this.nombreRol(b)));
    },
    hayFiltros() {
      const f = this.filtros;
      return Boolean(f.busqueda || f.Rol || f.ClubId || f.Activo !== null);
    },
    resumen() {
      if (this.cargando && !this.total) return "";
      const es = this.iso !== "en";
      if (this.hayFiltros) return es ? `${this.total} usuarios con estos filtros` : `${this.total} users match`;
      return es ? `${this.total} usuarios` : `${this.total} users`;
    },
  },
  async created() {
    this.$bus.on("change-iso", this.cambiarIdioma);
    await Promise.all([this.listarUsuarios(), this.listarRoles(), this.listarClubs(), this.listarIdiomas()]);
  },
  beforeDestroy() {
    this.$bus.off("change-iso", this.cambiarIdioma);
    clearTimeout(this.pausa);
  },
  methods: {
    cambiarIdioma() {
      this.iso = localStorage.getItem("iso") || "es";
    },
    esYo(usuario) {
      return Boolean(this.miId) && String(usuario?._id) === String(this.miId);
    },
    nombreRol(rol) {
      if (!rol) return "";
      return (this.iso === "en" && rol.NombreEn) || rol.Nombre || "";
    },
    nombreClub(id) {
      if (!id) return "—";
      return this.clubs.find((c) => String(c.Id) === String(id))?.Nombre || id;
    },
    hace(fecha) {
      return moment(fecha).locale(this.iso).fromNow();
    },
    fechaCompleta(fecha) {
      return moment(fecha).locale(this.iso).format("LLL");
    },
    fechaCorta(fecha) {
      return moment(fecha).locale(this.iso).format("ll");
    },
    mensajeError(err, porDefecto) {
      if (err?.response?.status === 401) return this.t.sesion[this.iso];
      return err?.response?.data?.error || porDefecto;
    },

    async listarUsuarios() {
      const consulta = ++this.consulta;
      this.cargando = true;
      try {
        const { busqueda, Rol, Activo, ClubId } = this.filtros;
        const { data } = await axios.post(
          `${API_URL}/system/list-users?page=${this.pagina}&limit=${this.porPagina}`,
          { busqueda, Rol, Activo, ClubId, orden: this.orden },
        );
        // Si el usuario siguió escribiendo, solo cuenta la última respuesta
        if (consulta !== this.consulta) return;
        this.usuarios = data?.users || [];
        this.total = data?.paginate?.total || 0;
      } catch (err) {
        if (consulta !== this.consulta) return;
        console.log(err);
        this.usuarios = [];
        this.total = 0;
        this.$buefy.toast.open({ message: this.mensajeError(err, this.t.errorLista[this.iso]), type: "is-danger" });
      } finally {
        if (consulta === this.consulta) this.cargando = false;
      }
    },
    buscar() {
      clearTimeout(this.pausa);
      this.pagina = 1;
      this.listarUsuarios();
    },
    buscarConPausa() {
      clearTimeout(this.pausa);
      this.pausa = setTimeout(this.buscar, 350);
    },
    limpiarFiltros() {
      this.filtros = { busqueda: "", Rol: null, Activo: null, ClubId: null };
      this.buscar();
    },
    cambiarPagina(pagina) {
      this.pagina = pagina;
      this.listarUsuarios();
    },
    cambiarPorPagina() {
      try {
        localStorage.setItem(CLAVE_POR_PAGINA, this.porPagina);
      } catch (e) {
        // sin almacenamiento: solo dura esta visita
      }
      this.buscar();
    },
    ordenar(campo, direccion) {
      this.orden = { campo, direccion };
      this.buscar();
    },

    async listarRoles() {
      try {
        // Todos, también los inactivos: hay usuarios con esos roles y se tienen que poder filtrar
        const { data } = await axios.post(`${API_URL}/system/list-roles`, {});
        this.roles = data?.roles || [];
      } catch (err) {
        console.log(err);
      }
    },
    async listarClubs() {
      try {
        const { data } = await axios.get(`${API_URL}/system/club/list`);
        this.clubs = (Array.isArray(data) ? data : data?.clubs || data?.data || []).sort((a, b) =>
          String(a.Nombre || "").localeCompare(String(b.Nombre || "")),
        );
      } catch (err) {
        console.log(err);
      }
    },
    async listarIdiomas() {
      try {
        const { data } = await axios.get(`${API_URL}/catalogue/CatIdiomas`);
        this.catalogues = { ...this.catalogues, CatIdiomas: data };
      } catch (err) {
        console.log(err);
      }
    },

    abrirAlta() {
      this.usuarioSeleccionado = null;
      this.modalUsuario = true;
    },
    abrirEdicion(usuario) {
      this.usuarioSeleccionado = usuario;
      this.modalUsuario = true;
    },
    cambiarEstado(usuario) {
      if (this.esYo(usuario)) return;
      const inhabilitar = usuario.Activo;
      const es = this.iso !== "en";
      const accion = inhabilitar ? this.t.inhabilitar[this.iso] : this.t.habilitar[this.iso];
      const quien = `<b>${escaparHtml(usuario.Usuario)}</b>`;
      this.$buefy.dialog.confirm({
        title: es ? `${accion} usuario` : `${accion} user`,
        message: inhabilitar
          ? es
            ? `${quien} ya no podrá iniciar sesión. ¿Inhabilitarlo?`
            : `${quien} won't be able to log in. Disable?`
          : es
          ? `${quien} podrá volver a iniciar sesión. ¿Habilitarlo?`
          : `${quien} will be able to log in again. Enable?`,
        confirmText: accion,
        cancelText: this.i18n.cancel[this.iso],
        type: inhabilitar ? "is-danger" : "is-success",
        hasIcon: true,
        onConfirm: async () => {
          try {
            await axios.put(`${API_URL}/system/update-user/${usuario._id}`, { Activo: !usuario.Activo });
            this.$buefy.toast.open({
              message: inhabilitar ? this.t.inhabilitado[this.iso] : this.t.habilitado[this.iso],
              type: "is-success",
            });
            this.listarUsuarios();
          } catch (err) {
            console.error(err);
            this.$buefy.toast.open({ message: this.mensajeError(err, accion), type: "is-danger" });
          }
        },
      });
    },
  },
};
</script>
